import type {
  FixtureMeta,
  ReportJson,
} from '../../../../../functions/tests/eval/phase1/types'

export interface LoadedFixture {
  slug: string
  meta: FixtureMeta
  inputText: string
  sourceUrl: string | null
}

export interface LoadedReport {
  filename: string
  startedAt: string
  data: ReportJson
}

// Eagerly bundle every fixture's meta.json, input.txt, optional source.png,
// and every result JSON. Vite resolves these globs at compile time, so HMR
// picks up edits to fixture files and freshly-written reports show up after
// a dev-server restart.
const metaModules = import.meta.glob<FixtureMeta>(
  '../../../../../functions/tests/eval/phase1/fixtures/*/meta.json',
  { eager: true, import: 'default' }
)
const inputModules = import.meta.glob<string>(
  '../../../../../functions/tests/eval/phase1/fixtures/*/input.txt',
  { eager: true, query: '?raw', import: 'default' }
)
const sourceModules = import.meta.glob<string>(
  '../../../../../functions/tests/eval/phase1/fixtures/*/source.png',
  { eager: true, query: '?url', import: 'default' }
)
const reportModules = import.meta.glob<ReportJson>(
  '../../../../../functions/tests/eval/phase1/results/*.json',
  { eager: true, import: 'default' }
)

function slugFromPath(path: string): string {
  const match = path.match(/fixtures\/([^/]+)\//)
  return match ? match[1] : path
}

export function loadFixtures(): LoadedFixture[] {
  const inputBySlug = new Map<string, string>()
  for (const [path, text] of Object.entries(inputModules)) {
    inputBySlug.set(slugFromPath(path), text)
  }
  const sourceBySlug = new Map<string, string>()
  for (const [path, url] of Object.entries(sourceModules)) {
    sourceBySlug.set(slugFromPath(path), url)
  }

  const fixtures: LoadedFixture[] = []
  for (const [path, meta] of Object.entries(metaModules)) {
    const slug = slugFromPath(path)
    fixtures.push({
      slug,
      meta,
      inputText: inputBySlug.get(slug) ?? '',
      sourceUrl: sourceBySlug.get(slug) ?? null,
    })
  }
  fixtures.sort((a, b) => a.slug.localeCompare(b.slug))
  return fixtures
}

export function loadReports(): LoadedReport[] {
  const reports: LoadedReport[] = []
  for (const [path, data] of Object.entries(reportModules)) {
    const filename = path.split('/').pop() ?? path
    reports.push({ filename, startedAt: data.startedAt, data })
  }
  // newest first
  reports.sort((a, b) => b.startedAt.localeCompare(a.startedAt))
  return reports
}
